/**
 * KpaAnalytics - Statistiques & analytics KPA Ton Concours
 * Recherches, chatbot, crédits, rétention, concours populaires
 */
import {
  AlertCircle,
  BarChart3,
  Bot,
  Loader2,
  RefreshCw,
  Search,
  TrendingUp,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface AnalyticsData {
  searches_today: number;
  searches_7d: number;
  searches_30d: number;
  chatbot_interactions_7d: number;
  chatbot_interactions_30d: number;
  active_users_7d: number;
  active_users_30d: number;
  total_users: number;
  new_users_7d: number;
  credits_consumed_7d: number;
  credits_purchased_7d: number;
  retention_rate_7d: number;
  top_categories: Array<{ category: string; count: number }>;
  top_concours: Array<{ title: string; saves: number; searches: number }>;
  daily_searches: Array<{ date: string; count: number }>;
  chatbot_by_topic: Array<{ topic: string; count: number }>;
}

const StatCard = ({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className="p-2 bg-gray-50 rounded-lg text-gray-400">{icon}</div>
    </div>
  </div>
);

const MiniBar = ({ label, value, max }: { label: string; value: number; max: number }) => (
  <div className="flex items-center gap-3 py-2">
    <span className="text-xs text-gray-600 w-32 truncate flex-shrink-0">{label}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-2">
      <div
        className="bg-orange-400 h-2 rounded-full transition-all duration-500"
        style={{ width: max > 0 ? `${Math.min(100, (value / max) * 100)}%` : '0%' }}
      />
    </div>
    <span className="text-xs font-semibold text-gray-700 w-8 text-right">{value}</span>
  </div>
);

const KpaAnalytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/pka/analytics`);
      if (!res.ok) throw new Error('Erreur lors du chargement des analytics');
      const json = await res.json();
      setData(json);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Fallback data for empty/error state to show layout  
  const d: AnalyticsData = data ?? {
    searches_today: 0, searches_7d: 0, searches_30d: 0,
    chatbot_interactions_7d: 0, chatbot_interactions_30d: 0,
    active_users_7d: 0, active_users_30d: 0, total_users: 0, new_users_7d: 0,
    credits_consumed_7d: 0, credits_purchased_7d: 0, retention_rate_7d: 0,
    top_categories: [], top_concours: [], daily_searches: [], chatbot_by_topic: [],
  };

  const maxCategory = Math.max(...d.top_categories.map(c => c.count), 1);
  const maxConcours = Math.max(...d.top_concours.map(c => c.saves), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-orange-500" />
            <h1 className="text-2xl font-bold text-gray-900">Analytics KPA</h1>
          </div>
          <p className="text-gray-500 text-sm">Recherches, chatbot IA, rétention, crédits consommés</p>
        </div>
        <button onClick={loadData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error} — Les endpoints analytics doivent être créés dans pka_routes.py
        </div>
      )}

      {/* KPIs principaux */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Recherches</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<Search className="w-4 h-4" />} label="Recherches aujourd'hui" value={d.searches_today} />
          <StatCard icon={<Search className="w-4 h-4" />} label="Recherches 7 jours" value={d.searches_7d} />
          <StatCard icon={<Search className="w-4 h-4" />} label="Recherches 30 jours" value={d.searches_30d} />
          <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Crédits consommés 7j" value={d.credits_consumed_7d} sub={`${d.credits_purchased_7d} achetés`} />
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Utilisateurs</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<Users className="w-4 h-4" />} label="Total inscrits" value={d.total_users} />
          <StatCard icon={<Users className="w-4 h-4" />} label="Actifs 7 jours" value={d.active_users_7d} sub={`${d.total_users > 0 ? Math.round((d.active_users_7d / d.total_users) * 100) : 0}% de la base`} />
          <StatCard icon={<Users className="w-4 h-4" />} label="Actifs 30 jours" value={d.active_users_30d} />
          <StatCard icon={<Users className="w-4 h-4" />} label="Nouveaux 7 jours" value={d.new_users_7d} />
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Chatbot IA</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<Bot className="w-4 h-4" />} label="Interactions 7 jours" value={d.chatbot_interactions_7d} />
          <StatCard icon={<Bot className="w-4 h-4" />} label="Interactions 30 jours" value={d.chatbot_interactions_30d} />
          <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Taux rétention 7j" value={`${d.retention_rate_7d}%`} />
          <StatCard
            icon={<BarChart3 className="w-4 h-4" />}
            label="Moy. interactions/user"
            value={d.active_users_7d > 0 ? (d.chatbot_interactions_7d / d.active_users_7d).toFixed(1) : '0'}
          />
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top catégories */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">Top catégories préférées</h3>
          {d.top_categories.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">Aucune donnée</p>
          ) : (
            <div>
              {d.top_categories.slice(0, 8).map((c) => (
                <MiniBar key={c.category} label={c.category} value={c.count} max={maxCategory} />
              ))}
            </div>
          )}
        </div>

        {/* Top concours sauvegardés */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">Concours les plus sauvegardés</h3>
          {d.top_concours.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">Aucune donnée</p>
          ) : (
            <div>
              {d.top_concours.slice(0, 8).map((c) => (
                <MiniBar key={c.title} label={c.title} value={c.saves} max={maxConcours} />
              ))}
            </div>
          )}
        </div>

        {/* Évolution recherches */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">Recherches — 30 derniers jours</h3>
          {d.daily_searches.length === 0 ? (
            <div className="text-gray-400 text-sm text-center py-8">
              <Search className="w-6 h-6 mx-auto mb-2" />
              Données disponibles via <code className="bg-gray-100 px-1 rounded">/api/pka/analytics</code>
            </div>
          ) : (
            <div className="flex items-end gap-1 h-24">
              {d.daily_searches.map((day, i) => {
                const maxVal = Math.max(...d.daily_searches.map(d => d.count), 1);
                return (
                  <div
                    key={i}
                    className="flex-1 bg-orange-400 rounded-t hover:bg-orange-500 transition"
                    style={{ height: `${(day.count / maxVal) * 100}%` }}
                    title={`${day.date}: ${day.count}`}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Chatbot par sujet */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">Sujets chatbot populaires</h3>
          {d.chatbot_by_topic.length === 0 ? (
            <div className="text-gray-400 text-sm text-center py-8">
              <Bot className="w-6 h-6 mx-auto mb-2" />
              Agrégation via table <code className="bg-gray-100 px-1 rounded">chatbot_usage</code>
            </div>
          ) : (
            <div>
              {d.chatbot_by_topic.slice(0, 6).map((t) => (
                <MiniBar
                  key={t.topic}
                  label={t.topic}
                  value={t.count}
                  max={Math.max(...d.chatbot_by_topic.map(x => x.count), 1)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Note technique */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700">
        <strong>Endpoints requis dans pka_routes.py :</strong>
        <ul className="mt-2 space-y-1 font-mono">
          <li>GET /api/pka/analytics — stats globales (searches, users, chatbot, credits)</li>
          <li>Source : agrégation des colonnes kpa_* + tables chatbot_usage + user_tracking_progress</li>
        </ul>
      </div>
    </div>
  );
};

export default KpaAnalytics;
